import * as React from 'react';
import { Box, Button, Container, Paper, Grid, Typography, TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Alert} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import * as moment from 'moment';
import { toast } from 'react-toastify';
import 'moment/locale/pt-br';

import PageDefault from '../../components/PageDefault';
import api from '../../services/api';
import apiDAM from '../../services/apiDAM';
import { getYears } from '../../services/helper';

export default function GerarCobrancaMassiva() {
    const [rua, setRua] = React.useState('');
    const [ano_ref, setAnoRef] = React.useState(moment().format('YYYY'));
    const [streets, setStreets] = React.useState([]);

    const [dialogMsg, setDialogMsg] = React.useState('');
    const [carregando, setCarregando] = React.useState(false);
    const [openDialog, setOpenDialog] = React.useState(false);

    const pollingTimerRef = React.useRef(null);
    const cancelledRef = React.useRef(false);

    const navigate = useNavigate();

    function handleBack() {
        navigate(-1);
    }

    React.useEffect(() => {
        cancelledRef.current = false;
        api.get('/street')
        .then(res => {
            const aux = [];            
            res.data.content.forEach(({ id, nome, bairro }) => {
                aux.push(nome)
            });
            setStreets(aux);
        })
        .catch(err => {
            if (err.response) {
                const message = err.response.data.message;
                toast.error(message);
            }
            setStreets([])
        });

        return () => {
            cancelledRef.current = true;
            if (pollingTimerRef.current) {
                clearTimeout(pollingTimerRef.current);
                pollingTimerRef.current = null;
            }
        };
    }, []);

    function isValid() {
        
        if (rua.trim() === '') {
            toast.error('Rua inválida');
            return false;
        }
        if (toString(ano_ref).trim() === '') {
            toast.error('Ano de Referência inválido');
            return false;
        }
        return true;
    }

    function gerarCobranca() {
        setOpenDialog(false);
        setCarregando(true);

        const dataPy = {
            'rua': rua,
            'ano_referencia': parseInt(ano_ref)
        }

        const headers = {
            'Content-Type': 'application/json'
        };

        const pollJobAndDownload = async (jobId) => {
            const poll = async () => {
                if (cancelledRef.current) {
                    return;
                }

                try {
                    const statusRes = await apiDAM.get(`/jobs/${jobId}`, { headers });
                    const job = statusRes.data;

                    if (job.status === 'done') {
                        const fileRes = await apiDAM.get(`/jobs/${jobId}/file`, {
                            headers,
                            responseType: 'blob'
                        });

                        const blob = new Blob([fileRes.data], { type: 'application/pdf' });
                        const url = window.URL.createObjectURL(blob);
                        window.open(url);
                        setCarregando(false);
                        return;
                    }

                    if (job.status === 'error') {
                        setCarregando(false);
                        toast.error(job.error || 'Erro ao gerar cobrança');
                        return;
                    }

                    pollingTimerRef.current = setTimeout(poll, 2000);
                } catch (err) {
                    setCarregando(false);
                    if (err.response && err.response.data && err.response.data.message) {
                        toast.error(err.response.data.message);
                    } else {
                        toast.error('Erro ao consultar status do job');
                    }
                }
            };

            await poll();
        };
        
        apiDAM.post(`/cobranca-massiva?com-aviso=True`, dataPy, {
            timeout: 1000 * 60,
            headers
        })
        .then(res => {
            const jobId = res.data && res.data.jobId;
            if (!jobId) {
                setCarregando(false);
                toast.error('Resposta inválida do servidor');
                return;
            }
            pollJobAndDownload(jobId);
        })
        .catch(err => {
            setCarregando(false);
            if (err.response && err.response.data && err.response.data.message) {
                toast.error(err.response.data.message);
            } else {
                toast.error('Erro ao iniciar geração da cobrança');
            }
        })
    }
        
    async function handleSubmit() {
        if (isValid()) {
            setDialogMsg(`Deseja realmente gerar a cobrança para os imóveis cadastrados na Rua ${rua}? *Essa ação é irreversível`);
            setOpenDialog(true);
        }
    }

    return (
        <PageDefault carregando={carregando} title='Geração de IPTU por Rua'>

            <Alert severity="warning" sx={{mt: 2}}>
                Recomendamos a geração massiva de IPTU por rua após o dia 01/12 do ano desejado
            </Alert>

            <Container component="main" maxWidth="sm" sx={{ mb: 4 }}>

                <Paper variant="outlined" sx={{ my: { xs: 3, md: 6 }, p: { xs: 2, md: 3 } }}>
                    <React.Fragment>
                        <Typography variant="h6" gutterBottom>
                            Dados da Cobrança
                        </Typography>
                        <Grid container spacing={3}>

                            <Grid item xs={12}>
                                <TextField
                                    required
                                    id="rua"
                                    name="rua"
                                    label="Rua"
                                    fullWidth
                                    select
                                    value={rua}
                                    onChange={e => setRua(e.target.value)}
                                >
                                    {streets.map(s => (
                                        <MenuItem value={s} key={s}>
                                            {s}
                                        </MenuItem>    
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    id="ano_ref"
                                    select
                                    fullWidth
                                    required
                                    label="Ano Referência"
                                    value={ano_ref}
                                    onChange={e => (
                                        setAnoRef(e.target.value)
                                    )}
                                >
                                    {getYears().map(ano => (
                                        <MenuItem value={ano} key={ano}>{ano}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                onClick={handleBack}
                                sx={{ mt: 3, ml: 1 }}>
                                Voltar
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                sx={{ mt: 3, ml: 1 }}
                            >
                                Gerar DAM
                            </Button>
                        </Box>
                    </React.Fragment>
                </Paper>
            </Container>
            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Confirmação de Ação
                </DialogTitle>
                <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    {dialogMsg}
                </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
                    <Button onClick={gerarCobranca} autoFocus>
                        Continuar
                    </Button>
                </DialogActions>
            </Dialog>
        </PageDefault>
    );
}